import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { map, find, forEach, cloneDeep, get as safeGet } from 'lodash';
import { TriangleDownOutlined, ArrowOutlined, MoreOutlined } from '@kufox/icons';
import { css } from '@kufox/mui/emotion';
import Dropdown from '@kufox/mui/Dropdown';
import useTheme from '@kufox/mui/hooks/useTheme';
import Accordion from '@kufox/mui/Accordion';
import { useTranslation } from '@tools/i18n';
import Link from '../../components/Link';
import { getHelpLang, NEWBIE_ZONE_ENTRY, genNewbieZoneItem } from './const';
import { namespace } from '../model';
import Point from '../../../static/newHeader/point.svg';

import {
  kcsensorsManualTrack,
  kcsensorsMenuClick as kcsensorsClick,
  addSpmIntoQuery,
  getMenuSpm,
} from '../../common/tools';

const { AccordionPanel: Panel } = Accordion;

const useStyles = ({ color, inDrawer }) => {
  return {
    nav: css`
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: ${inDrawer ? 'column' : 'row'};
      & > div {
        height: 100%;
      }
      & > div > div {
        height: 100%;
      }
    `,
    navItem: css`
      height: 100%;
      display: flex;
      align-items: center;
      position: relative;
      width: ${inDrawer ? '100%' : 'auto'};
      padding: ${inDrawer ? '10px 24px' : '0'};
      margin-right: 16px;
      font-size: ${inDrawer ? `14px` : `16px`};
      color: ${color.text};
      cursor: pointer;
      text-decoration: none !important;
      font-family: ${inDrawer ? 'URWDIN-Medium' : 'URWDIN'};
      white-space: nowrap;
      & .arrowIcon {
        margin-left: 3px;
      }
      &:hover {
        color: ${inDrawer ? color.text : color.text60};
        background: ${inDrawer ? 'rgba(115, 126, 141, 0.08)' : ''};
      }
      &:hover .arrowIcon {
        transform: rotate(180deg);
        margin-top: 5px;
      }
      &:svg {
        width: 10px;
        margin-left: 1px;
      }
      & .stressNav {
        height: 20px;
        line-height: 20px;
        font-size: 12px;
        color: #efa842;
        transform: scale(1) translateX(-50%);
        transform-origin: left;
        position: absolute;
        left: 50%;
        top: -17px;
        width: 100%;
        text-align: center;
        white-space: nowrap;
      }
      & .stressImg {
        height: 16px;
        width: 16px;
        position: absolute;
        right: -3px;
        top: -15px;
        object-fit: contain;
      }
      & .oneImg {
        right: 8px;
        top: -14px;
      }
      & .oneNav {
        top: -14px;
      }
    `,
    overlayWrapper: css`
      width: ${inDrawer ? '100%' : '400px'};
      margin-top: 0;
      padding: ${inDrawer ? '0 0' : '16px 0'};
      background: ${color.base};
      box-shadow: ${inDrawer ? 'none' : '0px 10px 60px rgba(0, 0, 0, 0.1)'};
      border-radius: ${inDrawer ? 0 : '4px'};
    `,
    moreOverlayWrapper: css`
      width: ${inDrawer ? '100%' : '800px'};
      margin-top: 0;
      padding: ${inDrawer ? '0 0' : '16px 0'};
      background: ${color.base};
      box-shadow: ${inDrawer ? 'none' : '0px 10px 60px rgba(0, 0, 0, 0.1)'};
      border-radius: ${inDrawer ? 0 : '4px'};
      display: flex;
      flex-wrap: wrap;
    `,
    multiOverlayWrapper: css`
      width: 1080px;
      margin-top: 20px;
      border-radius: 4px;
      padding: 8px 0px;
      background: ${color.base};
      display: flex;
    `,
    menuItem: css`
      width: ${inDrawer ? `100%` : '400px'};
      display: flex;
      align-items: center;
      padding: ${inDrawer ? '9px 24px' : '11px 32px 11px 16px'};
      cursor: pointer;
      color: ${color.text};
      text-decoration: none !important;
      position: relative;
      &:hover {
        background: rgba(115, 126, 141, 0.08);
        color: ${color.text};
      }
      @keyframes move {
        0% {
          opacity: 1;
          right: 20px;
        }
        100% {
          right: 18px;
        }
      }
      @keyframes changeColor {
        0% {
          fill: #737e8d;
        }
        100% {
          fill: #18bb97;
        }
      }
      & .arrow {
        display: none;
      }
      &:hover .arrow {
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 20px;
        animation: move 0.3s linear forwards;
        -webkit-animation: move 0.3s linear forwards;
        & path {
          animation: changeColor 0.3s linear forwards;
          -webkit-animation: changeColor 0.3s linear forwards;
        }
      }
      & .menuItemIcon {
        width: ${inDrawer ? '24px' : '36px'};
        height: ${inDrawer ? '24px' : '36px'};
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        border-radius: 4px;
        overflow: hidden;
        & img {
          width: ${inDrawer ? '24px' : '36px'};
          height: ${inDrawer ? '24px' : '36px'};
          object-fit: contain;
        }
      }
      & .menuItemBox {
        flex: 1;
        & .menuItemTitle {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          font-weight: ${inDrawer ? 400 : 500};
          font-size: ${inDrawer ? '14px' : '16px'};
          line-height: 26px;
          font-family: ${inDrawer ? 'URWDIN-Regular' : 'URWDIN'};
          & .menuItemName {
            max-width: 176px;
          }
          & .menuItemIcons {
            display: flex;
            align-items: center;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            word-break: break-all;
            max-width: 176px;
          }
          & .flag {
            border-radius: 2px;
            background: #29cd97;
            color: #ffffff;
            font-size: 12px;
            padding: 0 4px;
            height: 16px;
            line-height: 16px;
            position: relative;
            left: 6px;
            transform-origin: left;
            display: inline-block;
            margin-right: 6px;
            word-break: normal;
            min-width: 20px;
            align-items: center;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            word-break: break-all;
          }
          & .stress {
            background: linear-gradient(180deg, #edb66e 0%, #fff070 100%);
            border-radius: 2px;
            max-width: 112px;
            font-family: Helvetica;
            font-size: 12px;
            color: #01081e;
            padding: 3px 4px;
            min-height: 20px;
            line-height: 14px;
            position: relative;
            left: 10px;
            transform-origin: left;
            display: inline-block;
            word-break: normal;
          }
          & .stressIcon {
            height: 20px;
            position: relative;
            left: 4px;
            display: inline-block;
            object-fit: contain;
          }
        }
        & .menuItemSubTitle {
          color: ${inDrawer ? '#00142A' : color.text};
          opacity: 0.6;
          font-size: 12px;
          line-height: 20px;
        }
      }
    `,
    group: css`
      width: 360px;
      & .title {
        padding: 10px 0 0 28px;
        color: ${inDrawer ? '#00142A' : color.text};
        opacity: 0.6;
        font-size: 12px;
        line-height: 20px;
      }
    `,
    moreDiv: css`
      cursor: pointer;
      height: 100%;
      display: flex;
      align-items: center;
      padding-left: 20px;
      & .moreIcon {
        color: ${color.text};
      }
      &:hover .moreIcon {
        color: ${color.text60};
      }
      & .arrowIcon {
        margin: 0 20px 0 3px;
        color: #737e8d;
      }
      &:hover .arrowIcon {
        transform: rotate(180deg);
        margin-top: 5px;
      }
    `,
    navActiveBtn: css`
      cursor: pointer;
      margin: 0 8px;
      display: flex;
      align-items: center;
      height: 28px;
      max-width: 180px;
      padding: 0 9px;
      background: rgba(45, 189, 150, 0.12);
      border-radius: 8px;
      border: 1px solid transparent;
      color: #2dbd96;
      font-size: 14px;
      position: relative;
      text-decoration: none !important;
      font-family: ${inDrawer ? 'URWDIN-Medium' : 'URWDIN'};
      white-space: nowrap;
      & img {
        width: 10px;
        height: 10px;
        position: absolute;
        display: none;
        top: -5px;
        right: -5px;
      }
      &:hover {
        border-color: #2dbd96;
        color: #2dbd96;
        & img {
          display: block;
        }
      }
    `,
    marquee: css`
      width: 160px;
      white-space: nowrap;
      overflow: hidden;
      box-sizing: border-box;
      @keyframes marquee {
        0% {
          transform: translate(0, 0);
        }
        100% {
          transform: translate(-100%, 0);
        }
      }
      & p {
        display: inline-block;
        padding-left: 100%;
        animation: marquee 10s linear infinite;
      }
    `,
    newbie: css`
      font-size: 14px;
      line-height: 14px;
      color: #21c397;
      background: rgba(45, 189, 150, 0.12);
      border-radius: 8px;
      padding: 7px 10px;
      text-align: center;
      text-decoration: none;
    `,
    accordion: css`
      .KuxAccordion-head {
        padding: 8px 24px;
        font-size: 14px;
      }
    `,
  };
};

export default function Nav(props = {}) {
  const { customColors = {}, isSub, currentLang, inDrawer, hostConfig } = props;
  const { KUCOIN_HOST } = hostConfig || {};
  const theme = useTheme();
  const color = { ...theme.colors, ...customColors };
  const cls = useStyles({ color, breakpoints: theme.breakpoints, inDrawer });
  const { navList: _list, navMore, navActive, showNewbieNav } = useSelector(
    (state) => state[namespace],
  );
  const dispatch = useDispatch();
  const { t: _t } = useTranslation('header');
  const navList = cloneDeep(_list);
  if (navList && navList.length && showNewbieNav)
    navList.push(genNewbieZoneItem(_t, currentLang, KUCOIN_HOST));

  useEffect(() => {
    dispatch({
      type: `${namespace}/pullNavigationList`,
      payload: {
        type: isSub ? 3 : 2,
        lang: currentLang,
      },
    });
  }, [isSub, currentLang]);

  useEffect(() => {
    dispatch({
      type: `${namespace}/pullNewbieNavItem`,
    });
  }, []);

  // 判断当前url是.cc域名还是.com
  const getUrl = useCallback(
    (navItem) => {
      let jumpUrl = '';
      const { hostname } = window.location;
      // 针对帮助中心语言参数做特殊处理
      if (navItem.uri === 'https://support.kucoin.plus/hc') {
        jumpUrl = `${navItem.uri}/${getHelpLang[currentLang]}`;
      } else if (hostname.includes('.com')) {
        jumpUrl = `${navItem.uri}`;
      } else {
        jumpUrl = `${navItem.zhUri}`;
      }
      return jumpUrl;
    },
    [currentLang],
  );

  // 神策曝光埋点-无二级菜单-组件显示就立即上报
  useEffect(() => {
    if (navList && navList.length > 0) {
      forEach(navList, (navItem, navIndex) => {
        if (!navItem.children) {
          const menuSpm = getMenuSpm(navIndex, 0);
          const menuUrl = addSpmIntoQuery(getUrl(navItem), menuSpm, currentLang);
          kcsensorsManualTrack(menuSpm, {
            url: menuUrl,
            ButtonName: safeGet(navItem, 'buttonName'),
          });
        }
      });
    }
  }, [navList, getUrl, currentLang]);

  // 一级类目
  const genLinkNav = (navItem, index = 0) => {
    // const stressInfo = find(navItem.marks, (item) => item.type === 1) || {}; // 推广标示
    // const stressNav = stressInfo.position === 1 || stressInfo.position === 3 ? stressInfo.name : '';
    const _index = Number(index || 0) + 1;
    const menuSpm = getMenuSpm(index, 0);
    const menuUrl = addSpmIntoQuery(getUrl(navItem), menuSpm, currentLang);
    return (
      <Link
        key={navItem.id}
        href={menuUrl}
        lang={currentLang}
        data-ga={navItem.id}
        data-modid={`headers${_index}`}
        css={cls.navItem}
        onClick={
          navItem.onClick ||
          function() {
            kcsensorsClick(menuSpm, { url: menuUrl });
          }
        }
        style={{ marginRight: '20px' }}
      >
        {/* 展示自定义推广图片带文字 */}
        {/* {navItem.showMark && stressInfo.styleType === 2 && stressInfo.icons && (
    <img src={stressInfo.icons} alt="" className="stressImg" />
  )} */}
        {/* 展示默认推广文字 */}
        {/* {navItem.showMark && stressInfo.styleType === 1 && stressNav && (
    <span className="stressNav">{stressNav}</span>
  )} */}
        {navItem.name}
      </Link>
    );
  };

  // 一级类目有子菜单,提供给Drawer内部使用
  const genSingleCollapseNav = (navItem, index = 0) => {
    const _index = Number(index || 0) + 1;
    return (
      <Panel header={navItem.name} key={_index}>
        <Overlay items={navItem.children} modid={`headers${_index}`} />
      </Panel>
    );
  };
  // 满足显示时间超过300ms，才算曝光
  const exposeFlagMap = useRef({});
  // 二级菜单曝光
  const onSubMenuVisibleChange = useCallback(
    (menu, show, menuIndex) => {
      if (!menu) return;
      if (show && menu.children) {
        if (exposeFlagMap.current[menu.id]) {
          clearTimeout(exposeFlagMap.current[menu.id]);
        }
        exposeFlagMap.current[menu.id] = setTimeout(() => {
          forEach(menu.children, (subMenu, subIndex) => {
            const menuSpm = getMenuSpm(menuIndex, subIndex);
            const menuUrl = addSpmIntoQuery(getUrl(subMenu), menuSpm, currentLang);
            kcsensorsManualTrack(menuSpm, { url: menuUrl });
          });
          exposeFlagMap.current[menu.id] = null;
        }, 300);
      } else {
        const flag = exposeFlagMap.current[menu.id];
        if (flag) {
          clearTimeout(flag);
          exposeFlagMap.current[menu.id] = null;
        }
      }
    },
    [currentLang],
  );

  // 一级类目有子菜单
  const genSingleDropDownNav = (navItem, index = 0) => {
    const _index = Number(index || 0) + 1;
    return (
      <Dropdown
        trigger="hover"
        key={navItem.id}
        overlay={
          <Overlay items={navItem.children} modid={`headers${_index}`} parentIndex={index} />
        }
        onVisibleChange={(show) => {
          onSubMenuVisibleChange(navItem, show, index);
        }}
        placement="bottom-start"
        anchorProps={{ style: { 'display': 'block' } }}
      >
        <div css={cls.navItem}>
          {navItem.name}
          <TriangleDownOutlined size="11" className="arrowIcon" color="#737E8D" />
        </div>
      </Dropdown>
    );
  };

  const Overlay = ({ items, modid, parentIndex }) => {
    return (
      <div css={cls.overlayWrapper}>
        {map(items, (item, index) => {
          return LinkMenuItem({ ...item }, index, modid, parentIndex);
        })}
      </div>
    );
  };

  const MoreOverlay = ({ items, modid, parentIndex }) => {
    return (
      <div css={cls.moreOverlayWrapper}>
        {map(items, (item, index) => {
          return LinkMenuItem({ ...item }, index, modid, parentIndex);
        })}
      </div>
    );
  };

  const MoreComponent = () => {
    const index = 0;
    const _index = index + 1;
    if (navMore.children && navMore.children.length > 0) {
      return (
        <Dropdown
          trigger="hover"
          // key={navMore.id}
          overlay={
            <MoreOverlay
              items={navMore.children || []}
              modid={`headers${_index}`}
              parentIndex={index}
            />
          }
          onVisibleChange={(show) => {
            onSubMenuVisibleChange(navMore, show, index);
          }}
          placement="bottom-start"
          anchorProps={{ style: { 'display': 'block' } }}
        >
          <div css={cls.moreDiv}>
            <MoreOutlined className="moreIcon" width="13px" height="13px" />
            <TriangleDownOutlined size="11" className="arrowIcon" color="#737E8D" />
          </div>
        </Dropdown>
      );
    }
    return null;
  };
  const MoreInDrawerComponent = () => {
    const index = 0;
    const _index = index + 1;
    if (navMore.children && navMore.children.length > 0) {
      return (
        <Panel header={_t('more')} key={_index}>
          <Overlay items={navMore.children} modid={`headers${_index}`} parentIndex={index} />
        </Panel>
      );
    }
    return null;
  };
  const NavActiveComponent = (index) => {
    const navActiveItem =
      navActive.children && navActive.children.length > 0 ? navActive.children[0] : null;
    if (navActiveItem) {
      const _index = index + 1;
      const menuSpm = getMenuSpm(index, 0);
      const menuUrl = addSpmIntoQuery(getUrl(navActiveItem), menuSpm);
      const activeTag = document.getElementById('activeTag'); // 找到元素
      const isMarquee = !!(activeTag && activeTag.offsetWidth >= 180);
      return (
        <Link
          id="activeTag"
          href={menuUrl}
          css={cls.navActiveBtn}
          onClick={
            navActiveItem.onClick ||
            function() {
              kcsensorsClick(menuSpm, { url: menuUrl });
            }
          }
          data-ga={navActiveItem.id}
          data-modid={`headers${_index}`}
          lang={currentLang}
        >
          {isMarquee ? (
            <div css={cls.marquee}>
              <p>{navActiveItem.name}</p>
            </div>
          ) : (
            navActiveItem.name
          )}
          <img src={Point} alt="" />
        </Link>
      );
    }
    return null;
  };
  const getNewbieNav = (navItem, index = 0) => {
    const _index = Number(index || 0) + 1;
    return (
      <Link
        key={navItem?.id}
        href={navItem?.uri}
        lang={currentLang}
        data-ga={navItem?.id}
        data-modid={`headers${_index}`}
        css={inDrawer ? cls.navItem : cls.newbie}
        onClick={navItem.onClick}
      >
        {navItem.name}
      </Link>
    );
  };

  // 二级导航
  const LinkMenuItem = (navItem, index = 0, modid = '', parentIndex) => {
    const flagInfo = find(navItem.marks, (item) => item.type === 3) || {};
    const menuSpm = getMenuSpm(parentIndex, index);
    const menuUrl = addSpmIntoQuery(getUrl(navItem), menuSpm, currentLang);
    return (
      <Link
        key={navItem.id}
        href={menuUrl}
        css={cls.menuItem}
        onClick={
          navItem.onClick ||
          function() {
            kcsensorsClick(menuSpm, { url: menuUrl });
          }
        }
        data-ga={navItem.id}
        data-modid={modid}
        data-idx={index}
        lang={currentLang}
      >
        {!!navItem.icons && (
          <div className="menuItemIcon">
            <img src={navItem.icons} alt="" />
          </div>
        )}
        <div className="menuItemBox">
          <div className="menuItemTitle">
            <span
              className="menuItemName"
              style={navItem.showMark && flagInfo.name ? null : { maxWidth: 'unset' }}
            >
              {navItem.name || ''}
            </span>
            {!inDrawer && (
              <div className="menuItemIcons">
                {navItem.showMark && flagInfo.name ? (
                  <span className="flag" title={flagInfo.name}>
                    <span>{flagInfo.name}</span>
                  </span>
                ) : null}
              </div>
            )}
          </div>
          {!inDrawer && <div className="menuItemSubTitle">{navItem.explain}</div>}
        </div>
        <ArrowOutlined width="13px" height="13px" className="arrow" />
      </Link>
    );
  };
  if (inDrawer) {
    return (
      <Accordion css={cls.accordion}>
        {MoreInDrawerComponent()}
        {map(navList, (navItem, index) => {
          let comp;
          if (navItem.children) {
            comp = genSingleCollapseNav({ ...navItem }, index + 1);
          } else if (navItem.id === NEWBIE_ZONE_ENTRY) {
            comp = getNewbieNav({ ...navItem }, index + 1);
          } else {
            comp = genLinkNav({ ...navItem }, index + 1);
          }
          return comp;
        })}
      </Accordion>
    );
  }
  return (
    <div css={cls.nav}>
      {MoreComponent()}
      {map(navList, (navItem, index) => {
        let comp;
        if (navItem.children) {
          comp = genSingleDropDownNav({ ...navItem }, index + 1);
        } else if (navItem.id === NEWBIE_ZONE_ENTRY) {
          comp = getNewbieNav({ ...navItem }, index + 1);
        } else {
          comp = genLinkNav({ ...navItem }, index + 1);
        }
        return comp;
      })}
      {NavActiveComponent(navList.length + 1)}
    </div>
  );
}
